import { useRef, useState } from "react";
import { useTranslation } from "@/contexts/LanguageContext";
import { Monitor, Printer, Palette, Eye, Wrench, Lightbulb, TestTube, ArrowsClockwise } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { usePDFExport } from "@/utils/pdfExport";

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.05 } },
};
const itemAnim = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

// -- Live monitor test panels (only on screen, not in PDF) ------------------

function BlackLevelTest() {
  // Patches at 0 / 2 / 5 / 10 / 16 / 24 (out of 255). On a properly
  // calibrated monitor at moderate brightness, you should see distinct
  // separation between every patch. If 0/2/5 all look the same: black
  // crush. If you cannot see the lightest patch separated from gray: the
  // monitor's black level is too high.
  const levels = [0, 2, 5, 10, 16, 24];
  return (
    <div className="grid grid-cols-6 gap-1 p-3 bg-black rounded-md border border-white/10">
      {levels.map((l) => (
        <div
          key={l}
          className="h-12 rounded flex items-end justify-center pb-1"
          style={{ background: `rgb(${l},${l},${l})` }}
        >
          <span className="text-[9px] font-mono text-white/40">{l}</span>
        </div>
      ))}
    </div>
  );
}

function WhiteLevelTest() {
  const levels = [231, 239, 245, 250, 253, 255];
  return (
    <div className="grid grid-cols-6 gap-1 p-3 bg-white rounded-md border border-white/10">
      {levels.map((l) => (
        <div
          key={l}
          className="h-12 rounded flex items-end justify-center pb-1"
          style={{ background: `rgb(${l},${l},${l})` }}
        >
          <span className="text-[9px] font-mono text-black/40">{l}</span>
        </div>
      ))}
    </div>
  );
}

function GrayscaleRamp() {
  const stops = Array.from({ length: 17 }, (_, i) => Math.round((i / 16) * 255));
  return (
    <div className="flex h-14 rounded overflow-hidden border border-white/10">
      {stops.map((s) => (
        <div
          key={s}
          className="flex-1"
          style={{ background: `rgb(${s},${s},${s})` }}
          title={`${s}`}
        />
      ))}
    </div>
  );
}

function GammaTest() {
  // Three rows: gamma 1.8 / 2.2 / 2.4 squared targets. On a correctly
  // calibrated monitor at gamma 2.2, the *middle row* should appear most
  // uniform — the alternating stripes blend smoothly with the solid 50%
  // gray center patch.
  const rows = [
    { label: "Gamma 1.8 (Mac legacy)", center: "#94" },
    { label: "Gamma 2.2 (sRGB / Web standard)", center: "#bc" },
    { label: "Gamma 2.4 (HDTV / Rec.709)", center: "#dd" },
  ];
  return (
    <div className="space-y-2">
      {rows.map((r, idx) => {
        // Build a pattern using a striped div via a CSS gradient.
        const stripes =
          "repeating-linear-gradient(0deg, #000 0 1px, #fff 1px 2px)";
        return (
          <div
            key={idx}
            className="grid grid-cols-3 gap-0 h-16 rounded overflow-hidden border border-white/10"
          >
            <div style={{ background: stripes }} />
            <div style={{ background: r.center }} />
            <div style={{ background: stripes }} />
            <div className="col-span-3 text-[10px] text-[#A1A1AA] font-mono px-2 -mt-5 bg-black/60">
              {r.label}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ColorPatches() {
  const patches = [
    { label: "Red 100%", c: "#ff0000" },
    { label: "Green 100%", c: "#00ff00" },
    { label: "Blue 100%", c: "#0000ff" },
    { label: "Cyan", c: "#00ffff" },
    { label: "Magenta", c: "#ff00ff" },
    { label: "Yellow", c: "#ffff00" },
  ];
  return (
    <div className="grid grid-cols-3 gap-2">
      {patches.map((p) => (
        <div key={p.label} className="rounded overflow-hidden border border-white/10">
          <div className="h-12" style={{ background: p.c }} />
          <div className="bg-[#0A0A0A] px-2 py-1">
            <p className="text-[10px] text-[#A1A1AA] font-mono">{p.label}</p>
            <p className="text-[10px] text-[#71717A] font-mono">{p.c.toUpperCase()}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function SharpnessChart() {
  // Concentric circle / siemens-star approximation using SVG.
  return (
    <svg viewBox="0 0 200 80" className="w-full h-20 bg-white rounded border border-white/10">
      {Array.from({ length: 36 }).map((_, i) => {
        const angle = (i * 10 * Math.PI) / 180;
        const x1 = 100 + Math.cos(angle) * 6;
        const y1 = 40 + Math.sin(angle) * 6;
        const x2 = 100 + Math.cos(angle) * 36;
        const y2 = 40 + Math.sin(angle) * 36;
        return (
          <line
            key={i}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke={i % 2 === 0 ? "#000" : "transparent"}
            strokeWidth="3"
          />
        );
      })}
      {/* Resolution wedges */}
      {[10, 30, 50, 70, 90, 110, 130, 150, 170, 190].map((x, idx) => (
        <g key={x}>
          {Array.from({ length: 6 }).map((_, j) => (
            <rect
              key={j}
              x={x}
              y={10 + j * (3 + idx * 0.4)}
              width={Math.max(1, 8 - idx * 0.6)}
              height={2}
              fill="#000"
            />
          ))}
        </g>
      ))}
    </svg>
  );
}

// -- Content (EN + ES) ------------------------------------------------------

const CONTENT = {
  en: {
    page_title: "Monitor Calibration",
    page_sub:
      "If your screen lies, your prints will lie too. Learn why calibration matters, how to do it with hardware or software, and test your own monitor live with the interactive panels below.",
    download: "Download as PDF",
    generating: "Generating...",
    why_title: "Why calibration matters",
    why_intro:
      "Every monitor ships with a slightly different color response. Out-of-the-box screens are typically too bright, too cool (blue), and too contrasty for photo work. If you edit on an uncalibrated screen, the photos you 'fix' will look wrong everywhere else — too dark when printed, too warm on someone else's monitor, and unrecognizable on a phone.",
    why_points: [
      "Color accuracy: the colors you see match what your camera captured and what the lab prints",
      "Brightness consistency: a properly calibrated monitor at 80-120 cd/m2 ensures your shadows print as you intend",
      "White point: D65 (6500K) matches daylight viewing — the global standard for photo and print",
      "Gamma 2.2: ensures midtones distribute correctly between black and white (the sRGB / web standard)",
      "Repeatability: monitors drift over time — recalibrate every 4-6 weeks to keep the same look",
    ],
    color_space_title: "Color spaces — sRGB, Adobe RGB, DCI-P3",
    color_space_intro:
      "A color space defines the range of colors a device can show. Bigger gamut = more vivid greens and reds. Pick the space that matches your output destination.",
    color_spaces: [
      {
        name: "sRGB",
        coverage: "~72% of NTSC",
        for: "Web, social media, most consumer labs, phones, projectors. Default for everything online.",
      },
      {
        name: "Adobe RGB (1998)",
        coverage: "~99% of NTSC, ~50% wider greens",
        for: "Pro print labs, fine-art prints, commercial photography. Loses information when displayed in a sRGB browser.",
      },
      {
        name: "DCI-P3 / Display P3",
        coverage: "~25% larger than sRGB",
        for: "Modern Mac/iPad/iPhone screens, HDR video. Becoming the new web baseline.",
      },
      {
        name: "ProPhoto RGB",
        coverage: "Larger than visible spectrum",
        for: "Internal Lightroom/Camera Raw working space. Never deliver final files in ProPhoto.",
      },
    ],
    targets_title: "Calibration targets to remember",
    targets: [
      { label: "White point", value: "D65 (6500K)", note: "Daylight standard. Some print houses use D50 (5000K) — match the lab's lighting." },
      { label: "Brightness (luminance)", value: "80-120 cd/m2", note: "120 for daytime work, 80 for dim editing rooms. Higher = your prints will look too dark in real life." },
      { label: "Gamma", value: "2.2", note: "Standard for sRGB / web. macOS legacy was 1.8 — never use it for current work." },
      { label: "Black point", value: "0.3 cd/m2 or as low as possible", note: "Lower = deeper shadows. IPS panels often hit 0.4." },
      { label: "Recalibrate", value: "every 4-6 weeks", note: "Backlights drift. New panels: every 2 weeks for the first month." },
    ],
    methods_title: "Hardware vs. software calibration",
    hardware_title: "Hardware calibrator (recommended for serious work)",
    hardware_body:
      "A small puck-shaped device that hangs over your screen and reads actual emitted light. Software guides you through a 5-15 minute process, then writes a custom ICC profile for your screen. The only way to achieve true color accuracy.",
    hardware_tools: [
      "Calibrite Display Pro HL — modern replacement for X-Rite i1, ~$280",
      "Calibrite Display SL — entry level, ~$170",
      "SpyderX2 Elite — feature-rich, ~$320",
      "X-Rite i1Display Studio — legacy but supported, ~$180",
    ],
    software_title: "Software / visual calibration (free, less accurate)",
    software_body:
      "Built-in tools you can run today. Not as accurate as hardware (you are eyeballing it) but better than nothing. Useful as a starting point if you do not own a calibrator.",
    software_tools: [
      "Windows: Settings → Display → Calibrate display color (built-in wizard)",
      "macOS: System Settings → Displays → Color Profile → Customize → Calibrate",
      "Free download: DisplayCAL + a hardware probe (advanced, free open-source software)",
      "QuickGamma (Windows freeware): more precise gamma adjustment",
    ],
    environment_title: "Your viewing environment matters too",
    environment_points: [
      "Ambient light: dim, neutral. No colored walls behind your monitor — they reflect into your eyes and bias color judgment.",
      "Bias light: a small white LED strip behind the monitor (D65, ~6500K) reduces eye strain and improves perceived contrast.",
      "Avoid windows directly behind or across from the screen. If unavoidable, calibrate at the same time of day you edit.",
      "Wear neutral clothing during long sessions — a bright red shirt reflects in the screen and shifts your color perception.",
      "Take a 5-minute break every hour. Eye fatigue is your worst calibration enemy.",
    ],
    test_title: "Live monitor tests — try them on YOUR screen now",
    test_intro:
      "These tests use only standard CSS values, so they work on any monitor without downloads. Sit at your normal editing distance, dim the room a little, and walk through each one. They will reveal problems that a quick glance never catches.",
    tests: [
      {
        name: "Black level / shadow detail",
        instr: "On a properly calibrated screen, you should see clear separation between EVERY patch — including the lightest one against pure black. If patches 0/2/5 all look identical, your monitor is crushing shadows (raise brightness, or lower contrast).",
        comp: BlackLevelTest,
      },
      {
        name: "White level / highlight detail",
        instr: "Each patch should be slightly darker than the surrounding white. If you cannot see patches 250/253 distinct from 255, your monitor is clipping highlights — lower contrast or check ICC profile.",
        comp: WhiteLevelTest,
      },
      {
        name: "16-step grayscale ramp",
        instr: "The transitions should look perfectly smooth — no banding, no color casts (no green/magenta tinting in the grays). Banding indicates 6-bit panel limitations or a dirty calibration profile.",
        comp: GrayscaleRamp,
      },
      {
        name: "Gamma test",
        instr: "Step back from your screen 4-5 feet (1.5m) and squint. The CENTER row (gamma 2.2) should blend most smoothly between the black/white striped halves. If gamma 1.8 or 2.4 looks more uniform, your monitor's gamma is misset.",
        comp: GammaTest,
      },
      {
        name: "Color uniformity",
        instr: "Each pure color patch should look saturated and uniform across the full surface, not blotchy. Cover one with your hand — the surrounding patches should not shift hue. Bring your face close to each corner of the screen — colors should remain stable on a quality IPS panel.",
        comp: ColorPatches,
      },
      {
        name: "Sharpness / focus chart",
        instr: "All radial spokes should be crisp and clearly separated near the edge. The thinnest bars at the bottom corners should still be distinguishable. Blur means the monitor's scaling, anti-aliasing, or focus (CRT-era) is off.",
        comp: SharpnessChart,
      },
    ],
    troubleshoot_title: "Common problems and quick fixes",
    troubleshoot: [
      { p: "My calibrated monitor looks 'dull' compared to friends", a: "Theirs is uncalibrated and over-saturated. Yours is showing accurate color. Trust the calibration." },
      { p: "Prints come back darker than my screen", a: "Lower monitor brightness to 80-100 cd/m2. The classic mistake is editing on a screen at 200+ cd/m2." },
      { p: "Colors shift when I move my head", a: "TN panel limitation. Switch to an IPS or VA panel for photo work. Standard for any modern photo monitor." },
      { p: "Reds look orange / blues look purple", a: "Wrong color space. Check that your editor is set to sRGB or Adobe RGB and your image's embedded profile is correctly tagged." },
      { p: "Web exports look correct but print labs send wrong color", a: "You are exporting in Adobe RGB but the lab uses sRGB. Always Save-As → sRGB for consumer labs." },
    ],
    footer_note:
      "Calibrate once, recalibrate every 4-6 weeks, and view your prints under 5000K daylight-balanced bulbs. Do this and your edits will match your prints will match what every other professional sees on their own screen.",
  },
  es: {
    page_title: "Calibracion del Monitor",
    page_sub:
      "Si tu pantalla miente, tus impresiones tambien mentiran. Aprende por que importa la calibracion, como hacerla con hardware o software, y prueba tu propio monitor en vivo con los paneles interactivos abajo.",
    download: "Descargar como PDF",
    generating: "Generando...",
    why_title: "Por que importa la calibracion",
    why_intro:
      "Cada monitor sale de fabrica con una respuesta de color ligeramente distinta. Las pantallas nuevas suelen ser muy brillantes, muy frias (azuladas) y muy contrastadas para edicion. Si editas en una pantalla sin calibrar, las fotos que 'corriges' se veran mal en cualquier otra pantalla — demasiado oscuras al imprimir, calidas en otro monitor e irreconocibles en un movil.",
    why_points: [
      "Precision de color: lo que ves coincide con lo que captura tu camara y lo que imprime el laboratorio",
      "Consistencia de brillo: un monitor bien calibrado a 80-120 cd/m2 asegura que tus sombras impriman como las planeas",
      "Punto blanco: D65 (6500K) coincide con la luz de dia — estandar mundial para foto e impresion",
      "Gamma 2.2: asegura una distribucion correcta de medios tonos (estandar sRGB / web)",
      "Repetibilidad: los monitores derivan con el tiempo — recalibra cada 4-6 semanas",
    ],
    color_space_title: "Espacios de color — sRGB, Adobe RGB, DCI-P3",
    color_space_intro:
      "Un espacio de color define el rango de colores que un dispositivo puede mostrar. Mayor gamut = verdes y rojos mas vibrantes. Elige el que coincida con tu salida final.",
    color_spaces: [
      {
        name: "sRGB",
        coverage: "~72% de NTSC",
        for: "Web, redes sociales, laboratorios de consumo, telefonos, proyectores. Por defecto para todo en linea.",
      },
      {
        name: "Adobe RGB (1998)",
        coverage: "~99% de NTSC, ~50% mas verdes",
        for: "Laboratorios profesionales, impresion fine-art, fotografia comercial. Pierde informacion en navegadores sRGB.",
      },
      {
        name: "DCI-P3 / Display P3",
        coverage: "~25% mas grande que sRGB",
        for: "Pantallas modernas Mac/iPad/iPhone, video HDR. Se convierte en el nuevo estandar web.",
      },
      {
        name: "ProPhoto RGB",
        coverage: "Mas grande que el espectro visible",
        for: "Espacio de trabajo interno de Lightroom/Camera Raw. Nunca entregues archivos finales en ProPhoto.",
      },
    ],
    targets_title: "Objetivos de calibracion a recordar",
    targets: [
      { label: "Punto blanco", value: "D65 (6500K)", note: "Estandar de luz de dia. Algunos laboratorios usan D50 (5000K) — empareja con la luz del lab." },
      { label: "Brillo (luminancia)", value: "80-120 cd/m2", note: "120 para trabajo diurno, 80 para edicion en cuarto oscuro. Mas alto = tus impresiones se veran muy oscuras." },
      { label: "Gamma", value: "2.2", note: "Estandar para sRGB / web. El legado de macOS era 1.8 — no lo uses para trabajo actual." },
      { label: "Punto negro", value: "0.3 cd/m2 o tan bajo como sea posible", note: "Mas bajo = sombras mas profundas. Los paneles IPS suelen llegar a 0.4." },
      { label: "Recalibrar", value: "cada 4-6 semanas", note: "Las retroiluminaciones derivan. Paneles nuevos: cada 2 semanas el primer mes." },
    ],
    methods_title: "Calibracion por hardware vs software",
    hardware_title: "Calibrador por hardware (recomendado para trabajo serio)",
    hardware_body:
      "Un pequeno dispositivo en forma de disco que se cuelga sobre la pantalla y lee la luz emitida real. El software te guia por un proceso de 5-15 minutos y escribe un perfil ICC personalizado. La unica forma de lograr verdadera precision de color.",
    hardware_tools: [
      "Calibrite Display Pro HL — reemplazo moderno del X-Rite i1, ~$280",
      "Calibrite Display SL — gama de entrada, ~$170",
      "SpyderX2 Elite — completo, ~$320",
      "X-Rite i1Display Studio — legado pero soportado, ~$180",
    ],
    software_title: "Calibracion por software / visual (gratis, menos precisa)",
    software_body:
      "Herramientas integradas que puedes usar hoy. No tan precisas como el hardware (estas calibrando a ojo) pero mejor que nada. Util como punto de partida si no tienes calibrador.",
    software_tools: [
      "Windows: Configuracion → Pantalla → Calibrar el color de la pantalla (asistente integrado)",
      "macOS: Ajustes del Sistema → Pantallas → Perfil de Color → Personalizar → Calibrar",
      "Descarga gratuita: DisplayCAL + sonda de hardware (avanzado, software libre)",
      "QuickGamma (freeware Windows): ajuste de gamma mas preciso",
    ],
    environment_title: "Tu entorno de visualizacion tambien importa",
    environment_points: [
      "Luz ambiente: tenue y neutral. Sin paredes de colores detras del monitor — reflejan y sesgan el juicio de color.",
      "Luz bias: una tira LED blanca pequena detras del monitor (D65, ~6500K) reduce la fatiga visual y mejora el contraste percibido.",
      "Evita ventanas directamente detras o frente a la pantalla. Si es inevitable, calibra a la misma hora a la que editas.",
      "Usa ropa neutral en sesiones largas — una camisa roja brillante se refleja en la pantalla y desvia tu percepcion del color.",
      "Toma 5 minutos de descanso por hora. La fatiga ocular es el peor enemigo de la calibracion.",
    ],
    test_title: "Pruebas en vivo del monitor — pruebalas en TU pantalla ahora",
    test_intro:
      "Estas pruebas usan solo valores CSS estandar, asi que funcionan en cualquier monitor sin descargas. Sientate a tu distancia normal de edicion, baja un poco la luz del cuarto y recorre cada una. Revelaran problemas que una mirada rapida nunca detecta.",
    tests: [
      {
        name: "Nivel de negro / detalle en sombras",
        instr: "En una pantalla bien calibrada deberias ver separacion clara entre CADA parche — incluido el mas claro contra el negro puro. Si los parches 0/2/5 lucen identicos, tu monitor esta aplastando sombras (sube brillo o baja contraste).",
        comp: BlackLevelTest,
      },
      {
        name: "Nivel de blanco / detalle en luces",
        instr: "Cada parche debe ser ligeramente mas oscuro que el blanco circundante. Si no distingues los parches 250/253 de 255, tu monitor recorta las luces — baja el contraste o revisa el perfil ICC.",
        comp: WhiteLevelTest,
      },
      {
        name: "Rampa de grises de 16 pasos",
        instr: "Las transiciones deben verse perfectamente suaves — sin bandas, sin tintes de color (sin verde/magenta en los grises). Las bandas indican panel de 6 bits o un perfil de calibracion sucio.",
        comp: GrayscaleRamp,
      },
      {
        name: "Prueba de gamma",
        instr: "Aleja la cabeza de la pantalla 1.5m y entrecierra los ojos. La fila CENTRAL (gamma 2.2) debe fundirse mas suavemente entre las mitades rayadas blanco/negro. Si gamma 1.8 o 2.4 luce mas uniforme, la gamma de tu monitor esta mal.",
        comp: GammaTest,
      },
      {
        name: "Uniformidad de color",
        instr: "Cada parche puro debe verse saturado y uniforme en toda su superficie, sin manchas. Cubre uno con la mano — los parches vecinos no deberian cambiar de tono. Acerca el rostro a cada esquina de la pantalla — en un IPS de calidad los colores se mantienen estables.",
        comp: ColorPatches,
      },
      {
        name: "Carta de nitidez / enfoque",
        instr: "Todos los radios deben verse nitidos y bien separados cerca del borde. Las barras mas finas en las esquinas inferiores aun deben distinguirse. Si hay desenfoque, el escalado, antialiasing o enfoque del monitor esta mal.",
        comp: SharpnessChart,
      },
    ],
    troubleshoot_title: "Problemas comunes y soluciones rapidas",
    troubleshoot: [
      { p: "Mi monitor calibrado se ve 'apagado' frente al de mis amigos", a: "El de ellos esta sin calibrar y sobre-saturado. El tuyo muestra color preciso. Confia en la calibracion." },
      { p: "Las impresiones llegan mas oscuras que mi pantalla", a: "Baja el brillo del monitor a 80-100 cd/m2. El error clasico es editar a mas de 200 cd/m2." },
      { p: "Los colores cambian al mover la cabeza", a: "Limitacion de panel TN. Cambia a IPS o VA para foto. Estandar en cualquier monitor moderno de foto." },
      { p: "Los rojos se ven anaranjados / azules muy violetas", a: "Espacio de color erroneo. Verifica que el editor este en sRGB o Adobe RGB y la imagen tenga el perfil incrustado correcto." },
      { p: "Las exportaciones web se ven bien pero el lab manda color erroneo", a: "Estas exportando en Adobe RGB pero el lab usa sRGB. Siempre Guardar Como → sRGB para labs de consumo." },
    ],
    footer_note:
      "Calibra una vez, recalibra cada 4-6 semanas y revisa tus impresiones bajo focos de luz dia 5000K. Hazlo y tus ediciones coincidiran con tus impresiones y con lo que cualquier otro profesional ve en su pantalla.",
  },
};

export default function CalibrationPage() {
  const { lang } = useTranslation();
  const c = CONTENT[lang] || CONTENT.en;
  const printRef = useRef(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const { ready, generating, download } = usePDFExport(
    printRef,
    () => `monitor-calibration-${lang}.pdf`,
    [lang]
  );

  return (
    <div
      className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 print:bg-white print:text-black"
      data-testid="calibration-page"
    >
      <div className="print:hidden">
        <motion.div variants={container} initial="hidden" animate="show" className="space-y-8">
          <motion.div variants={itemAnim} className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold text-white tracking-tight mb-2">
                {c.page_title}
              </h1>
              <p className="text-[#71717A] text-base max-w-3xl">{c.page_sub}</p>
            </div>
            <Button
              onClick={download}
              disabled={!ready}
              className="bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-medium disabled:opacity-60"
              data-testid="calibration-download-btn"
            >
              <Printer className="w-4 h-4 mr-1.5" />
              {generating ? c.generating : c.download}
            </Button>
          </motion.div>

          {/* Why */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-2">
              <Eye weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.why_title}</h2>
            </div>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-3">{c.why_intro}</p>
            <ul className="space-y-2">
              {c.why_points.map((p, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="mt-2 w-1 h-1 rounded-full bg-[#F5A524] shrink-0" />
                  <span className="text-[#A1A1AA] text-sm leading-relaxed">{p}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Color Spaces */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-2">
              <Palette weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.color_space_title}</h2>
            </div>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-4">{c.color_space_intro}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {c.color_spaces.map((cs, i) => (
                <div key={i} className="bg-[#0A0A0A] border border-white/10 rounded-lg p-3">
                  <p className="text-[#F5A524] font-['JetBrains_Mono'] text-sm font-semibold mb-1">{cs.name}</p>
                  <p className="text-[#71717A] text-xs mb-1">{cs.coverage}</p>
                  <p className="text-[#A1A1AA] text-xs leading-relaxed">{cs.for}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Targets */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-2">
              <Wrench weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.targets_title}</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <tbody>
                  {c.targets.map((t, i) => (
                    <tr key={i} className="border-b border-white/5">
                      <td className="py-3 pr-3 text-white font-medium align-top w-1/4">{t.label}</td>
                      <td className="py-3 pr-3 text-[#F5A524] font-['JetBrains_Mono'] align-top w-1/4">{t.value}</td>
                      <td className="py-3 text-[#A1A1AA] text-xs leading-relaxed">{t.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>

          {/* Hardware vs Software */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-2">
              <Monitor weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.methods_title}</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-[#0A0A0A] border border-[#F5A524]/30 rounded-lg p-4">
                <p className="text-[#F5A524] font-medium text-sm mb-2">{c.hardware_title}</p>
                <p className="text-[#A1A1AA] text-xs leading-relaxed mb-3">{c.hardware_body}</p>
                <ul className="space-y-1">
                  {c.hardware_tools.map((tool, i) => (
                    <li key={i} className="text-[#D4D4D8] text-xs font-['JetBrains_Mono'] flex gap-2">
                      <span className="text-[#F5A524]">•</span> {tool}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-4">
                <p className="text-white font-medium text-sm mb-2">{c.software_title}</p>
                <p className="text-[#A1A1AA] text-xs leading-relaxed mb-3">{c.software_body}</p>
                <ul className="space-y-1">
                  {c.software_tools.map((tool, i) => (
                    <li key={i} className="text-[#D4D4D8] text-xs font-['JetBrains_Mono'] flex gap-2">
                      <span className="text-[#71717A]">•</span> {tool}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Environment */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-2">
              <Lightbulb weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.environment_title}</h2>
            </div>
            <ul className="space-y-2">
              {c.environment_points.map((p, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="mt-2 w-1 h-1 rounded-full bg-[#F5A524] shrink-0" />
                  <span className="text-[#A1A1AA] text-sm leading-relaxed">{p}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* LIVE TESTS */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-[#F5A524]/30 rounded-xl p-6">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <div className="flex items-center gap-2">
                <TestTube weight="fill" className="w-5 h-5 text-[#F5A524]" />
                <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.test_title}</h2>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setRefreshKey((k) => k + 1)}
                className="text-[#A1A1AA] hover:text-[#F5A524] hover:bg-white/5"
                data-testid="calibration-refresh-tests"
              >
                <ArrowsClockwise className="w-4 h-4 mr-1.5" />
                {lang === "es" ? "Refrescar" : "Refresh"}
              </Button>
            </div>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-5">{c.test_intro}</p>

            <div className="space-y-5" key={refreshKey}>
              {c.tests.map((test, i) => {
                const Comp = test.comp;
                return (
                  <div
                    key={i}
                    className="bg-[#0A0A0A] border border-white/10 rounded-lg p-4"
                    data-testid={`calibration-test-${i}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-6 h-6 rounded-full bg-[#F5A524] text-[#0A0A0A] flex items-center justify-center text-xs font-bold">
                        {i + 1}
                      </span>
                      <h3 className="text-white font-medium text-sm">{test.name}</h3>
                    </div>
                    <Comp />
                    <p className="text-[#A1A1AA] text-xs leading-relaxed mt-3 italic">{test.instr}</p>
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* Troubleshoot */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <h2 className="font-['Outfit'] text-white text-xl font-medium mb-3">
              {c.troubleshoot_title}
            </h2>
            <div className="space-y-3">
              {c.troubleshoot.map((t, i) => (
                <div key={i} className="bg-[#0A0A0A] border border-white/10 rounded-lg p-3">
                  <p className="text-white text-sm font-medium mb-1">Q: {t.p}</p>
                  <p className="text-[#A1A1AA] text-xs leading-relaxed">A: {t.a}</p>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.p
            variants={itemAnim}
            className="text-[#A1A1AA] text-sm italic text-center max-w-3xl mx-auto leading-relaxed"
          >
            "{c.footer_note}"
          </motion.p>
        </motion.div>
      </div>

      {/* Print / PDF (no live tests in PDF — they require a screen) */}
      <div ref={printRef} className="hidden print:block bg-white text-black p-4" data-testid="calibration-print-view">
        <h1 className="text-3xl font-bold text-center mb-1">{c.page_title}</h1>
        <p className="text-center text-gray-500 mb-5 text-sm">{c.page_sub}</p>

        <div className="mb-6 break-inside-avoid">
          <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">1. {c.why_title}</h2>
          <p className="text-sm text-gray-700 mb-2">{c.why_intro}</p>
          <ul className="list-disc ml-5 space-y-1">
            {c.why_points.map((p, i) => (
              <li key={i} className="text-xs text-gray-700">{p}</li>
            ))}
          </ul>
        </div>

        <div className="mb-6 break-inside-avoid">
          <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">2. {c.color_space_title}</h2>
          <p className="text-sm text-gray-700 mb-3">{c.color_space_intro}</p>
          <table className="w-full text-xs border-collapse mb-2">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 p-2 text-left">Space</th>
                <th className="border border-gray-300 p-2 text-left">Coverage</th>
                <th className="border border-gray-300 p-2 text-left">Use</th>
              </tr>
            </thead>
            <tbody>
              {c.color_spaces.map((cs, i) => (
                <tr key={i}>
                  <td className="border border-gray-300 p-2 font-mono font-semibold">{cs.name}</td>
                  <td className="border border-gray-300 p-2">{cs.coverage}</td>
                  <td className="border border-gray-300 p-2">{cs.for}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mb-6 break-inside-avoid">
          <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">3. {c.targets_title}</h2>
          <table className="w-full text-xs border-collapse">
            <tbody>
              {c.targets.map((t, i) => (
                <tr key={i}>
                  <td className="border border-gray-300 p-2 font-semibold">{t.label}</td>
                  <td className="border border-gray-300 p-2 font-mono">{t.value}</td>
                  <td className="border border-gray-300 p-2 italic text-gray-600">{t.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mb-6 break-inside-avoid">
          <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">4. {c.methods_title}</h2>
          <p className="text-sm font-semibold mt-3 mb-1">{c.hardware_title}</p>
          <p className="text-xs text-gray-700 mb-2">{c.hardware_body}</p>
          <ul className="list-disc ml-5 space-y-1 mb-3">
            {c.hardware_tools.map((tool, i) => (
              <li key={i} className="text-xs text-gray-700 font-mono">{tool}</li>
            ))}
          </ul>
          <p className="text-sm font-semibold mt-3 mb-1">{c.software_title}</p>
          <p className="text-xs text-gray-700 mb-2">{c.software_body}</p>
          <ul className="list-disc ml-5 space-y-1">
            {c.software_tools.map((tool, i) => (
              <li key={i} className="text-xs text-gray-700 font-mono">{tool}</li>
            ))}
          </ul>
        </div>

        <div className="mb-6 break-inside-avoid">
          <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">5. {c.environment_title}</h2>
          <ul className="list-disc ml-5 space-y-1">
            {c.environment_points.map((p, i) => (
              <li key={i} className="text-xs text-gray-700">{p}</li>
            ))}
          </ul>
        </div>

        <div className="mb-6 break-inside-avoid">
          <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">6. {c.test_title}</h2>
          <p className="text-xs text-gray-700 mb-2 italic">
            {lang === "es"
              ? "Estas pruebas requieren ejecutarse en pantalla — visita la version online para hacerlas:"
              : "These tests must be run on-screen — visit the online version to perform them:"}
            {" "}
            <span className="font-mono">www.dslradvisor.com/calibration</span>
          </p>
          <ol className="list-decimal ml-5 space-y-2">
            {c.tests.map((t, i) => (
              <li key={i} className="text-xs text-gray-700">
                <strong>{t.name}:</strong> {t.instr}
              </li>
            ))}
          </ol>
        </div>

        <div className="mb-6 break-inside-avoid">
          <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">7. {c.troubleshoot_title}</h2>
          {c.troubleshoot.map((t, i) => (
            <div key={i} className="mb-2">
              <p className="text-sm font-semibold">Q: {t.p}</p>
              <p className="text-xs text-gray-700">A: {t.a}</p>
            </div>
          ))}
        </div>

        <p className="text-sm text-gray-600 italic text-center max-w-2xl mx-auto leading-relaxed mt-8">
          "{c.footer_note}"
        </p>
        <p className="text-center text-xs text-gray-400 mt-4">www.dslradvisor.com</p>
      </div>
    </div>
  );
}
